import React from 'react';
import { useToast } from '../components/ToastProvider';

const PackagesPage: React.FC = () => {
  const { addToast } = useToast();

  const packages = [
    {
      id: 'starter',
      name: 'Starter',
      price: '$999',
      popular: false,
      description: 'Perfect for small businesses and startups looking to establish their online presence.',
      features: [
        '5-page responsive website',
        'Modern, mobile-first design',
        'Basic SEO optimization',
        'Contact form integration',
        'Social media links',
        '30 days of support',
        'SSL certificate included',
        'Fast loading optimization'
      ],
      buttonText: 'Get Started',
      buttonStyle: 'bg-serenity-blue text-white hover:bg-blue-700'
    },
    {
      id: 'professional',
      name: 'Professional',
      price: '$2,499',
      popular: true,
      description: 'Ideal for growing businesses that need advanced features and functionality.',
      features: [
        '10-page responsive website',
        'Custom design & branding',
        'Advanced SEO optimization',
        'Content management system',
        'Blog functionality',
        'E-commerce capability (up to 50 products)',
        'Google Analytics integration',
        'Email marketing setup',
        '90 days of support',
        'Performance monitoring'
      ],
      buttonText: 'Choose Professional',
      buttonStyle: 'bg-gradient-to-r from-serenity-blue to-blue-700 text-white hover:from-blue-700 hover:to-blue-800'
    },
    {
      id: 'enterprise',
      name: 'Enterprise',
      price: '$4,999',
      popular: false,
      description: 'Comprehensive solution for large businesses requiring custom development.',
      features: [
        'Unlimited pages',
        'Full custom development',
        'Advanced integrations',
        'Multi-language support',
        'Advanced e-commerce features',
        'Custom admin dashboard',
        'API development',
        'Database design',
        'Security hardening',
        '6 months of support',
        'Priority support',
        'Performance guarantees'
      ],
      buttonText: 'Contact Sales',
      buttonStyle: 'bg-serenity-dark text-white hover:bg-gray-800'
    }
  ];

  const handleSelectPackage = (packageName: string, packageId: string) => {
    // This would typically integrate with a payment processor like Stripe
    addToast({
      type: 'info',
      title: 'Package Selected',
      message: `You selected the ${packageName} package. Redirecting to payment...`,
      duration: 3000
    });

    // Simulate redirect to payment
    setTimeout(() => {
      addToast({
        type: 'warning',
        title: 'Payment Integration Pending',
        message: 'Payment integration will be available in Phase 2 (Backend)',
        duration: 5000
      });
    }, 3000);
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-serenity-dark mb-6">
            Choose Your Perfect Package
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Whether you're just starting out or scaling up, we have the perfect solution 
            to bring serenity to your digital presence.
          </p>
        </div>

        {/* Packages Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {packages.map((pkg) => (
            <div
              key={pkg.id}
              className={`relative bg-white rounded-2xl shadow-xl overflow-hidden transform hover:scale-105 transition-all duration-300 ${
                pkg.popular ? 'ring-4 ring-serenity-blue ring-opacity-50' : ''
              }`}
            >
              {/* Popular Badge */}
              {pkg.popular && (
                <div className="absolute top-0 right-0 bg-gradient-to-r from-serenity-blue to-blue-700 text-white px-4 py-2 rounded-bl-2xl font-semibold text-sm">
                  Most Popular
                </div>
              )}

              <div className="p-8">
                {/* Package Header */}
                <div className="text-center mb-8">
                  <h3 className="text-2xl font-bold text-serenity-dark mb-2">
                    {pkg.name}
                  </h3>
                  <div className="text-4xl font-bold text-serenity-blue mb-4">
                    {pkg.price}
                  </div>
                  <p className="text-gray-600">
                    {pkg.description}
                  </p>
                </div>

                {/* Features List */}
                <ul className="space-y-3 mb-8">
                  {pkg.features.map((feature, index) => (
                    <li key={index} className="flex items-start">
                      <svg
                        className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>

                {/* CTA Button */}
                <button
                  onClick={() => handleSelectPackage(pkg.name, pkg.id)}
                  className={`w-full py-4 px-6 rounded-xl font-semibold text-lg transition-all duration-300 ${pkg.buttonStyle} shadow-lg hover:shadow-xl`}
                >
                  {pkg.buttonText}
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Additional Info */}
        <div className="mt-16 text-center">
          <div className="bg-white rounded-2xl shadow-lg p-8 max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-serenity-dark mb-6">
              All Packages Include
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="text-center">
                <div className="w-12 h-12 bg-serenity-blue rounded-full flex items-center justify-center mx-auto mb-3">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="font-semibold text-serenity-dark">Quality Guarantee</h3>
                <p className="text-sm text-gray-600 mt-1">100% satisfaction guaranteed</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-serenity-blue rounded-full flex items-center justify-center mx-auto mb-3">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <h3 className="font-semibold text-serenity-dark">Fast Delivery</h3>
                <p className="text-sm text-gray-600 mt-1">Project completion in 2-4 weeks</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-serenity-blue rounded-full flex items-center justify-center mx-auto mb-3">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192L5.636 18.364M12 12h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="font-semibold text-serenity-dark">Ongoing Support</h3>
                <p className="text-sm text-gray-600 mt-1">Dedicated support team</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-serenity-blue rounded-full flex items-center justify-center mx-auto mb-3">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <h3 className="font-semibold text-serenity-dark">Secure & Reliable</h3>
                <p className="text-sm text-gray-600 mt-1">Enterprise-grade security</p>
              </div>
            </div>
          </div>
        </div>

        {/* Custom Solution CTA */}
        <div className="mt-12 text-center">
          <div className="bg-gradient-to-r from-serenity-blue to-blue-700 rounded-2xl text-white p-8">
            <h2 className="text-2xl font-bold mb-4">
              Need Something Custom?
            </h2>
            <p className="text-lg mb-6 text-blue-100">
              Have unique requirements? Let's discuss a custom solution tailored specifically for your business needs.
            </p>
            <button
              onClick={() => addToast({
                type: 'info',
                title: 'Custom Solution',
                message: 'Please use the contact form to discuss your custom requirements.',
                duration: 4000
              })}
              className="bg-white text-serenity-blue px-8 py-3 rounded-lg font-semibold hover:bg-blue-50 transition-colors duration-300"
            >
              Discuss Custom Solution
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PackagesPage;
